import { useQuery } from "@apollo/client";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { StatCard, StatCardRowSkeleton } from "components/StatCard";
import { FacilityAccumulationSummaryQuery } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import pluralize from "pluralize";
import { Link as RouterLink } from "react-router-dom";

import { ComplianceFindingBadges } from "../../Compliance/ComplianceFindingBadge";
import {
  FACILITY_CAP_KINDS,
  useEntityComplianceFindings,
} from "../../Compliance/useEntityComplianceFindings";
import { FACILITY_ACCUMULATION_SUMMARY_QUERY } from "../../Containers/api";
import { formatContainerDate } from "../../Containers/containerDisplay";
import { isEventWindowLapsed } from "../../EpisodicEvents/episodicDisplay";
import { deriveOnSiteCapDisplay, formatKg } from "./onSiteCapDisplay";
import { useWasteFacilityState } from "./WasteFacilityContext";

type AccumulationSummary = NonNullable<
  NonNullable<FacilityAccumulationSummaryQuery["facility"]>["accumulationSummary"]
>;

/**
 * Containers & accumulation summary for the waste facility dashboard: stat
 * cards for the active container count and the on-site hazardous total vs
 * the generator-status cap, plus a link to the facility-scoped container
 * list.
 */
type ActiveEpisodicEvent = NonNullable<
  NonNullable<FacilityAccumulationSummaryQuery["facility"]>["episodicEvents"]
>["items"][number];

/** The facility's current active episodic event, if any, for the banner. */
function selectActiveEpisodicEvent(
  data: FacilityAccumulationSummaryQuery | undefined
): ActiveEpisodicEvent | undefined {
  return data?.facility?.episodicEvents?.items[0];
}

/**
 * Banner copy for the active-episodic-event callout. The exemption the
 * summary resolver grants is revoked once the event's accumulation window
 * has lapsed but the event hasn't been swept/closed yet
 * (`isEventWindowLapsed`), so the banner must stop asserting an exemption
 * that no longer applies and instead say the window ended. `now` is
 * injectable for tests.
 */
export function activeEventBannerMessage(
  event: Pick<ActiveEpisodicEvent, "reason" | "accumulationDeadline">,
  now: DateTime = DateTime.now()
): string {
  const deadline = formatContainerDate(event.accumulationDeadline);
  if (isEventWindowLapsed(event.accumulationDeadline, now)) {
    return `Episodic event active: ${event.reason} — its accumulation window ended ${deadline} and its waste no longer counts as exempt from the on-site cap.`;
  }
  return `Episodic event active: ${event.reason} — remove its waste by ${deadline}. Waste tied to the event doesn't count toward the on-site cap.`;
}

export const WasteContainersTile = () => {
  const { tenantId } = useTenant();
  const { effectiveWasteFacilityId } = useWasteFacilityState();

  const { data, loading } = useQuery(FACILITY_ACCUMULATION_SUMMARY_QUERY, {
    variables: { facilityId: effectiveWasteFacilityId },
    skip: !effectiveWasteFacilityId,
    // Revalidate on mount so the on-site total reflects containers added from
    // other routes (create-container's refetch only hits this query while this
    // tile is mounted); cache-first alone leaves a stale total until hard refresh.
    fetchPolicy: "cache-and-network",
  });
  const summary = data?.facility?.accumulationSummary;
  const activeEvent = selectActiveEpisodicEvent(data);
  // The tile's mount is already ContainerTracking-gated in WasteFacility.
  const { forFacility } = useEntityComplianceFindings({
    facilityId: effectiveWasteFacilityId,
    kinds: FACILITY_CAP_KINDS,
  });

  return (
    <DashboardTile size={{ xs: 12 }}>
      <DashboardTile.Header title="Containers & accumulation" />
      <DashboardTile.ContentArea>
        <Stack spacing={2} alignItems="stretch" sx={{ width: "100%" }}>
          {loading && summary == null ? (
            <StatCardRowSkeleton count={5} />
          ) : (
            <AccumulationStatRow summary={summary} />
          )}
          <ComplianceFindingBadges findings={forFacility()} />
          {summary && (
            <SummaryCallouts
              summary={summary}
              activeEvent={activeEvent}
              tenantId={tenantId}
              facilityId={effectiveWasteFacilityId}
            />
          )}
          <Button
            component={RouterLink}
            to={`/o/${tenantId}/waste/containers?facilityId=${effectiveWasteFacilityId}`}
            variant="text"
            size="small"
            endIcon={<ArrowForward />}
            sx={{ alignSelf: "flex-start" }}
          >
            View containers
          </Button>
        </Stack>
      </DashboardTile.ContentArea>
    </DashboardTile>
  );
};

/**
 * The kg-on-site stat: the big number is the hazardous mass currently on
 * site; the bottom caption names the cap it's judged against — SQG/VSQG get
 * their cap, LQGs have none (time-limited instead), and an unresolvable
 * generator status shows no cap (the callout below prompts for setup). Once
 * an active episodic event exempts some of that mass, the caption switches
 * to the judged math and a sub-caption states the exempt amount.
 */
const OnSiteTotalStatCard = ({ summary }: { summary: AccumulationSummary | undefined }) => {
  const totalOnSiteKg = (summary?.nonAcuteOnSiteKg ?? 0) + (summary?.acuteOnSiteKg ?? 0);
  const display = deriveOnSiteCapDisplay(summary);

  return (
    <StatCard
      value={formatKg(totalOnSiteKg)}
      caption={display.caption}
      // The acute line keeps its decimals: it's judged against a 1 kg cap,
      // where rounding 0.5 kg up to "1 kg" would misread as at-cap.
      subCaption={display.acuteCaption}
      extraCaption={display.exemptCaption}
      valueColor={summary?.overCap ? "error.main" : "text.primary"}
      subCaptionColor={display.acuteOver ? "error.main" : "text.secondary"}
    />
  );
};

const SummaryCallouts = ({
  summary,
  activeEvent,
  tenantId,
  facilityId,
}: {
  summary: AccumulationSummary;
  activeEvent: ActiveEpisodicEvent | undefined;
  tenantId: string | undefined;
  facilityId: string;
}) => (
  <>
    {activeEvent && (
      <Alert
        severity="info"
        action={
          <Button
            color="inherit"
            size="small"
            component={RouterLink}
            to={`/o/${tenantId}/waste/episodic-events?facilityId=${facilityId}`}
          >
            View event
          </Button>
        }
      >
        {activeEventBannerMessage(activeEvent)}
      </Alert>
    )}
    {summary.generatorStatus == null && (
      <Alert severity="info">Set your generator status to see the facility's on-site cap.</Alert>
    )}
    {summary.overCap && <Alert severity="error">Over the on-site accumulation cap</Alert>}
    {summary.assumedDensityContainerCount > 0 && (
      <Typography variant="caption" color="warning.main">
        {summary.assumedDensityContainerCount}{" "}
        {pluralize("container", summary.assumedDensityContainerCount)} converted using the density
        of water — totals may be inaccurate until{" "}
        {summary.assumedDensityContainerCount === 1 ? "its" : "their"} waste{" "}
        {pluralize("stream", summary.assumedDensityContainerCount)}{" "}
        {summary.assumedDensityContainerCount === 1 ? "has" : "have"} density information
      </Typography>
    )}
  </>
);

/** A count that reads as 0 rather than blank when the summary has not loaded. */
const count = (value: number | undefined) => value ?? 0;

/**
 * Non-zero counts are the ones worth looking at, so they take a semantic color
 * and everything else stays neutral. A helper rather than a ternary per card:
 * the rule is the same on every one of them.
 */
const emphasizeIfAny = (value: number | undefined, color: string) =>
  count(value) > 0 ? color : "text.primary";

/**
 * The facility's on-site numbers at a glance. Its own component because each
 * card carries a "highlight when non-zero" rule, and eight of those inline made
 * the tile itself hard to read.
 */
const AccumulationStatRow = ({ summary }: { summary: AccumulationSummary | undefined }) => (
  <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
    <StatCard
      value={String(count(summary?.activeContainerCount))}
      caption={`Active ${pluralize("container", count(summary?.activeContainerCount))}`}
    />
    <StatCard
      value={String(count(summary?.approachingDeadlineContainerCount))}
      caption="Approaching deadline"
      valueColor={emphasizeIfAny(summary?.approachingDeadlineContainerCount, "warning.main")}
    />
    <StatCard
      value={String(count(summary?.overdueContainerCount))}
      caption="Overdue"
      valueColor={emphasizeIfAny(summary?.overdueContainerCount, "error.main")}
    />
    <OnSiteTotalStatCard summary={summary} />
    <StatCard
      value={String(count(summary?.overThresholdAreaCount))}
      caption={`Satellite ${pluralize("area", count(summary?.overThresholdAreaCount))} over limit`}
      valueColor={emphasizeIfAny(summary?.overThresholdAreaCount, "error.main")}
    />
    {/* Warning, not error: the vendor re-weighing or re-classifying at
            the TSDF is normal and the manifest is the record of what
            actually left. This is something to review, not to fix. */}
    <StatCard
      value={String(count(summary?.discrepantShipmentCount))}
      caption={`${pluralize("Shipment", count(summary?.discrepantShipmentCount))} differing from manifest`}
      valueColor={emphasizeIfAny(summary?.discrepantShipmentCount, "warning.main")}
    />
  </Stack>
);
